import { pb } from './Pocketbase'
import {
  createBrand,
  createCountry,
  createPerfumer,
  createNote
} from './PocketbaseMetadataService'
import { getPendingSubmissionById, deletePendingSubmission } from './PocketbasePendingSubmissionsService'
import { normalizeString, generateFragranceSlug } from '../utils/stringUtils'

export interface IApprovedSubmission {
  id: string
  name: string
  slug: string
  brand: string // relation to brands
  country: string // relation to countries
  perfumers: string[] // relation to perfumers (multiple)
  price: string // relation to prices
  top_notes: string[] // relation to notes (multiple)
  middle_notes: string[] // relation to notes (multiple)
  bottom_notes: string[] // relation to notes (multiple)
  description: string
  year: number
  image: string // file field
  submitted_by: string // relation to users
  created: string
}


/**
 * Ensures a brand exists in the database
 * If brand is already a relation ID, returns it
 * If brand_text is provided, checks if it exists or creates it
 */
const ensureBrand = async (
  brandId: string | undefined,
  brandText: string | undefined
): Promise<string> => {
  // If we have a brand relation, use it
  if (brandId) {
    return brandId
  }

  // If we have brand_text, check if it exists or create it
  if (brandText) {
    const normalizedName = normalizeString(brandText)

    // Query by normalized_name for exact match
    const existing = await pb.collection('brands').getFirstListItem(
      `normalized_name = "${normalizedName}"`,
      { $autoCancel: false }
    ).catch(() => null)

    if (existing) {
      return existing.id
    }

    // Create new brand
    const newBrand = await createBrand(brandText)
    return newBrand.id
  }

  throw new Error('Brand is required but not provided')
}

/**
 * Ensures a country exists in the database
 * If country is already a relation ID, returns it
 * If country_text is provided, checks if it exists or creates it
 */
const ensureCountry = async (
  countryId: string | undefined,
  countryText: string | undefined
): Promise<string> => {
  // If we have a country relation, use it
  if (countryId) {
    return countryId
  }

  // If we have country_text, check if it exists or create it
  if (countryText) {
    const normalizedName = normalizeString(countryText)

    // Query by normalized_name for exact match
    const existing = await pb.collection('countries').getFirstListItem(
      `normalized_name = "${normalizedName}"`,
      { $autoCancel: false }
    ).catch(() => null)

    if (existing) {
      return existing.id
    }

    // Create new country
    const newCountry = await createCountry(countryText)
    return newCountry.id
  }

  throw new Error('Country is required but not provided')
}

/**
 * Ensures perfumers exist in the database
 * For relation IDs, returns them as-is
 * For custom text perfumers, checks if they exist or creates them
 * Returns array of perfumer IDs
 */
const ensurePerfumers = async (
  perfumerIds: string[] | undefined,
  perfumerTexts: string[] | undefined
): Promise<string[]> => {
  const resultIds: string[] = []

  // Add existing relation IDs
  if (perfumerIds && perfumerIds.length > 0) {
    resultIds.push(...perfumerIds)
  }

  // Process custom text perfumers
  if (perfumerTexts && perfumerTexts.length > 0) {
    for (const perfumerText of perfumerTexts) {
      const normalizedName = normalizeString(perfumerText)

      // Query by normalized_name for exact match
      const existing = await pb.collection('perfumers').getFirstListItem(
        `normalized_name = "${normalizedName}"`,
        { $autoCancel: false }
      ).catch(() => null)

      if (existing) {
        resultIds.push(existing.id)
      } else {
        // Create new perfumer
        const newPerfumer = await createPerfumer(perfumerText)
        resultIds.push(newPerfumer.id)
      }
    }
  }

  return resultIds
}

/**
 * Ensures notes exist in the database
 * For relation IDs, returns them as-is
 * For custom text notes, checks if they exist or creates them
 * Returns array of note IDs
 */
const ensureNotes = async (
  noteIds: string[] | undefined,
  noteTexts: string[] | undefined
): Promise<string[]> => {
  const resultIds: string[] = []

  // Add existing relation IDs
  if (noteIds && noteIds.length > 0) {
    resultIds.push(...noteIds)
  }

  // Process custom text notes
  if (noteTexts && noteTexts.length > 0) {
    for (const noteText of noteTexts) {
      const normalizedName = normalizeString(noteText)

      // Query by normalized_name for exact match
      const existing = await pb.collection('notes').getFirstListItem(
        `normalized_name = "${normalizedName}"`,
        { $autoCancel: false }
      ).catch(() => null)

      if (existing) {
        resultIds.push(existing.id)
      } else {
        // Create new note
        const newNote = await createNote(noteText)
        resultIds.push(newNote.id)
      }
    }
  }

  return resultIds
}

/**
 * Helper to parse JSON arrays from pending submission
 */
const parseJsonArray = (value: string[] | string | null | undefined): string[] => {
  if (!value) return []
  if (Array.isArray(value)) return value

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/**
 * Approves a pending submission by:
 * 1. Creating any missing metadata (brands, countries, perfumers, notes)
 * 2. Creating an approved submission with all required relations
 * 3. Deleting the pending submission from the database
 * 
 * @param pendingSubmissionId - ID of the pending submission to approve
 * @returns The created approved submission
 */
export const approvePendingSubmission = async (
  pendingSubmissionId: string
): Promise<IApprovedSubmission> => {
  try {
    // 1. Fetch the pending submission
    const pendingSubmission = await getPendingSubmissionById(pendingSubmissionId)

    // 2. Validate required fields
    if (!pendingSubmission.name) {
      throw new Error('Submission name is required')
    }
    if (!pendingSubmission.description) {
      throw new Error('Submission description is required')
    }
    if (!pendingSubmission.year) {
      throw new Error('Submission year is required')
    }
    if (!pendingSubmission.image) {
      throw new Error('Submission image is required')
    }
    if (!pendingSubmission.submitted_by) {
      throw new Error('Submission submitted_by is required')
    }
    if (!pendingSubmission.price) {
      throw new Error('Submission price is required')
    }

    // 3. Ensure all metadata exists (create if needed)
    const brandId = await ensureBrand(
      pendingSubmission.brand,
      pendingSubmission.brand_text
    )

    // Get brand name for slug generation
    const brandRecord = await pb.collection('brands').getOne(brandId)
    const brandName = brandRecord.name as string

    const countryId = await ensureCountry(
      pendingSubmission.country,
      pendingSubmission.country_text
    )

    // Parse perfumer arrays
    const perfumerIds = Array.isArray(pendingSubmission.perfumers)
      ? pendingSubmission.perfumers
      : (pendingSubmission.perfumers ? [pendingSubmission.perfumers] : [])
    const perfumerTexts = parseJsonArray(pendingSubmission.perfumers_text)

    const perfumers = await ensurePerfumers(perfumerIds, perfumerTexts)

    // Generate slug from brand name and fragrance name
    const slug = generateFragranceSlug(brandName, pendingSubmission.name)

    // Parse note arrays
    const topNoteIds = Array.isArray(pendingSubmission.top_notes)
      ? pendingSubmission.top_notes
      : (pendingSubmission.top_notes ? [pendingSubmission.top_notes] : [])
    const middleNoteIds = Array.isArray(pendingSubmission.middle_notes)
      ? pendingSubmission.middle_notes
      : (pendingSubmission.middle_notes ? [pendingSubmission.middle_notes] : [])
    const bottomNoteIds = Array.isArray(pendingSubmission.bottom_notes)
      ? pendingSubmission.bottom_notes
      : (pendingSubmission.bottom_notes ? [pendingSubmission.bottom_notes] : [])

    const topNoteTexts = parseJsonArray(pendingSubmission.top_notes_text)
    const middleNoteTexts = parseJsonArray(pendingSubmission.middle_notes_text)
    const bottomNoteTexts = parseJsonArray(pendingSubmission.bottom_notes_text)

    const topNotes = await ensureNotes(topNoteIds, topNoteTexts)
    const middleNotes = await ensureNotes(middleNoteIds, middleNoteTexts)
    const bottomNotes = await ensureNotes(bottomNoteIds, bottomNoteTexts)

    // 4. Download the image from pending submission
    const imageUrl = pb.files.getURL(pendingSubmission, pendingSubmission.image)
    const imageResponse = await fetch(imageUrl)
    const imageBlob = await imageResponse.blob()
    const imageFile = new File([imageBlob], pendingSubmission.image, { type: imageBlob.type })

    // 5. Create the approved submission
    const formData = new FormData()
    formData.append('name', pendingSubmission.name)
    formData.append('normalized_name', normalizeString(pendingSubmission.name))
    // slug is handled per-case (create/update)
    formData.append('brand', brandId)
    formData.append('country', countryId)
    formData.append('price', pendingSubmission.price)
    formData.append('description', pendingSubmission.description)
    formData.append('year', pendingSubmission.year.toString())
    formData.append('image', imageFile)
    formData.append('submitted_by', pendingSubmission.submitted_by)

    // Add perfumers (multiple relations)
    perfumers.forEach(perfumerId => formData.append('perfumers', perfumerId))

    // Add notes (multiple relations)
    topNotes.forEach(noteId => formData.append('top_notes', noteId))
    middleNotes.forEach(noteId => formData.append('middle_notes', noteId))
    bottomNotes.forEach(noteId => formData.append('bottom_notes', noteId))

    let approvedSubmission: IApprovedSubmission

    // Check if this is an edit of an existing approved submission
    if (pendingSubmission.approved_ref) {
      // Update the existing approved submission (excluding slug updates)
      await pb.collection('approved_submissions').update<IApprovedSubmission>(
        pendingSubmission.approved_ref,
        formData
      )

      // Handle slug updates and redirects separately
      // This will only update the slug and create a redirect if the slug has actually changed
      approvedSubmission = await updateApprovedSubmissionSlug(
        pendingSubmission.approved_ref,
        brandName,
        pendingSubmission.name
      )
    } else {
      // Add slug for new submission
      formData.append('slug', slug)

      // Create a new approved submission
      approvedSubmission = await pb.collection('approved_submissions').create<IApprovedSubmission>(formData)

      // Create a redirect entry for the slug
      await createFragranceRedirect(slug, approvedSubmission.id)
    }

    // Delete the pending submission
    await deletePendingSubmission(pendingSubmissionId)

    return approvedSubmission

  } catch (error) {
    console.error('Error approving submission:', error)
    throw error
  }
}

/**
 * Fetches approved submissions with pagination and expanded relations
 */
export const getApprovedSubmissions = async (
  page: number = 1,
  perPage: number = 10
): Promise<{
  items: IApprovedSubmissionExpanded[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}> => {
  const result = await pb.collection('approved_submissions').getList<IApprovedSubmissionExpanded>(page, perPage, {
    sort: '-created',
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes'
  })
  return result
}

/**
 * Searches approved submissions by name with pagination and expanded relations
 */
export const searchApprovedSubmissions = async (
  page: number = 1,
  perPage: number = 10,
  search: string
): Promise<{
  items: IApprovedSubmissionExpanded[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}> => {
  const filter = pb.filter('name ~ {:search}', { search })
  const result = await pb.collection('approved_submissions').getList<IApprovedSubmissionExpanded>(page, perPage, {
    sort: '-created',
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes',
    filter
  })
  return result
}

/**
 * Fetches all approved submissions (no pagination)
 */
export const getAllApprovedSubmissions = async (): Promise<IApprovedSubmission[]> => {
  const records = await pb.collection('approved_submissions').getFullList<IApprovedSubmission>({
    sort: '-created'
  })
  return records
}

/**
 * Fetches a single approved submission by ID
 */
export const getApprovedSubmissionById = async (id: string): Promise<IApprovedSubmission> => {
  const record = await pb.collection('approved_submissions').getOne<IApprovedSubmission>(id)
  return record
}

/**
 * Interface for approved submission with expanded relations
 */
export interface IApprovedSubmissionExpanded extends Omit<IApprovedSubmission, 'brand' | 'country' | 'perfumers' | 'price' | 'top_notes' | 'middle_notes' | 'bottom_notes'> {
  brand: string
  country: string
  perfumers: string[]
  price: string
  top_notes: string[]
  middle_notes: string[]
  bottom_notes: string[]
  expand?: {
    brand?: { id: string; name: string }
    country?: { id: string; name: string }
    perfumers?: { id: string; name: string }[]
    price?: { id: string; name: string }
    top_notes?: { id: string; name: string }[]
    middle_notes?: { id: string; name: string }[]
    bottom_notes?: { id: string; name: string }[]
  }
}

/**
 * Fetches a single approved submission by slug with expanded relations
 * First checks the redirect_fragrance table, then falls back to direct slug match
 */
export const getApprovedSubmissionBySlug = async (slug: string): Promise<IApprovedSubmissionExpanded> => {
  const expandFields = 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes'

  // First, check the redirect table
  try {
    const redirect = await pb.collection('redirect_fragrance').getFirstListItem<{
      id: string
      slug: string
      fragrance: string
    }>(
      pb.filter('slug = {:slug}', { slug }),
      { $autoCancel: false }
    )

    // Found a redirect, fetch the fragrance by ID with expanded relations
    return await pb.collection('approved_submissions').getOne<IApprovedSubmissionExpanded>(
      redirect.fragrance,
      { expand: expandFields }
    )
  } catch {
    // No redirect found, try direct slug match on approved_submissions
    const record = await pb.collection('approved_submissions').getFirstListItem<IApprovedSubmissionExpanded>(
      pb.filter('slug = {:slug}', { slug }),
      { $autoCancel: false, expand: expandFields }
    )
    return record
  }
}

/**
 * Creates a redirect entry mapping a slug to a fragrance
 */
export const createFragranceRedirect = async (slug: string, fragranceId: string): Promise<void> => {
  await pb.collection('redirect_fragrance').create({
    slug,
    fragrance: fragranceId
  })
}

/**
 * Updates the slug for an approved submission and creates a redirect from the old slug
 */
export const updateApprovedSubmissionSlug = async (
  submissionId: string,
  newBrandName: string,
  newFragranceName: string
): Promise<IApprovedSubmission> => {
  // Get the current submission
  const current = await pb.collection('approved_submissions').getOne<IApprovedSubmission>(submissionId)
  const oldSlug = current.slug

  // Generate new slug
  const newSlug = generateFragranceSlug(newBrandName, newFragranceName)

  // If slug hasn't changed, no need to update
  if (oldSlug === newSlug) {
    return current
  }

  // Update the submission with new slug
  const updated = await pb.collection('approved_submissions').update<IApprovedSubmission>(submissionId, {
    slug: newSlug
  })

  // Create a new redirect for the new slug
  await createFragranceRedirect(newSlug, submissionId)

  // Note: The old slug redirect still exists and points to the same fragrance ID,
  // so old URLs will continue to work

  return updated
}

/**
 * Deletes an approved submission
 */
export const deleteApprovedSubmission = async (id: string): Promise<void> => {
  await pb.collection('approved_submissions').delete(id)
}